import React from 'react';
import { motion } from 'framer-motion';
import { Feather, Sun, Compass, Heart } from 'lucide-react';

const BenefitsSection = () => {
  const benefits = [
    {
      num: '01',
      title: 'Peaceful Environment',
      description: 'Create a calm, quiet, and serene atmosphere within your living or sacred space.',
      icon: Feather,
    },
    {
      num: '02',
      title: 'Positive Energy',
      description: 'Bring an uplifting, harmonious, and positive vibration into your everyday routine.',
      icon: Sun,
    },
    {
      num: '03',
      title: 'Spiritual Connection',
      description: 'Keep meaningful Himalayan spiritual elements close to you throughout your busy day.',
      icon: Compass,
    },
    {
      num: '04',
      title: 'Meaningful Presence',
      description: 'Let authentic divine products become a cherishable part of your home and personal journey.',
      icon: Heart,
    },
  ];

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-12 bg-[#F3E7D0] relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-[#E8D5A8]/30 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        
        {/* LEFT COLUMN: Large Lifestyle Image */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border-4 border-[#FBF4E7] shadow-md">
            <img
              src="https://images.unsplash.com/photo-1577516311194-eb14c570a137?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Peaceful Divine Surroundings"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#4E1225]/75 via-transparent to-transparent" />
            
            <div className="absolute bottom-6 left-6 right-6 text-[#FBF4E7]">
              <span className="text-xs uppercase tracking-widest text-[#E8D5A8] font-bold">SACRED HARMONY</span>
              <p className="font-serif-heading text-2xl font-bold mt-1">
                "Sanctuary for the Soul"
              </p>
            </div>
          </div>
        </motion.div>

        {/* RIGHT COLUMN: Editorial Content */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 space-y-8"
        >
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#B85A15]">
              DIVINE LIFESTYLE & WELL-BEING
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#4E1225] mt-2 leading-tight">
              Invite Peace, Positivity & Divinity Into Your Space
            </h2>
            <p className="text-[#83725B] text-base sm:text-lg mt-3 font-normal leading-relaxed">
              More than beautiful objects, divine products can become meaningful reminders of peace, intention and spiritual connection in everyday life.
            </p>
          </div>

          {/* Benefit Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
            {benefits.map((benefit, index) => {
              const IconComp = benefit.icon;
              return (
                <motion.div
                  key={benefit.num}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="p-5 rounded-xl bg-[#FBF4E7] border border-[#E7D9BC] shadow-xs space-y-2.5"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#B98B3E] tracking-wider">
                      {benefit.num}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-[#6E1B34]/10 flex items-center justify-center text-[#6E1B34]">
                      <IconComp className="w-4 h-4" />
                    </div>
                  </div>

                  <h3 className="font-serif-heading text-base font-bold text-[#4E1225]">
                    {benefit.title}
                  </h3>

                  <p className="text-xs text-[#83725B] leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default BenefitsSection;
